import { Tooltip } from "antd";
import { HighlightText } from "./HighlightedText";
import dayjs from "dayjs";
import { SortIconColumn, TableTdWrapper, Header, ActionsColumn } from "../pages/Home/HomeStyled";
import { Box } from "@mui/material";
import SortIcon from "../assets/images/sorting.svg";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
dayjs.extend(relativeTime);
dayjs.extend(utc);

export const renderFileName = (text, search) => (
    <Tooltip placement="rightTop" title={text}>
      <TableTdWrapper>{HighlightText(text, search)}</TableTdWrapper>
    </Tooltip>
  );
  
  export const renderDate = (text) => (
    <span>{text ? dayjs(text).format("DD MMM YYYY") : "-"}</span>
  );
  
export const renderDueOn = (_, record) => {
  if (
    record.due_on &&
    (record.status === "PENDING" || record?.status === "REWORK")
  ) {
    const date = dayjs.utc(record.due_on);
    const now = dayjs.utc();
    if (date.isBefore(now)) {
      return <span style={{ color: "#B31514" }}>{date.fromNow()}</span>;
    } else {
      return <span>{date.format("DD MMM YYYY")}</span>;
    }
  }
};

  export const renderText = (text) => <span>{text}</span>;

  export const renderPendingWithText = (text) => <span>{text.length > 0 ? text : "-"}</span>;
  
  // Helper function to render status with consistent styling
  const renderStatusWithClass = (statusClass, displayText) => (
    <ActionsColumn>
      <span className={statusClass}>{displayText}</span>
    </ActionsColumn>
  );
  
  // Helper function to get status class and display text
  const getStatusInfo = (text, type) => {
    const veteranStatusMap = {
      received: { statusClass: "pending", displayText: "Received" },
      pending: { statusClass: "vaPending", displayText: "Pending" },
      uploaded: { statusClass: "rework", displayText: "Uploaded" },
      processing: { statusClass: "rework", displayText: "Processing" },
      success: { statusClass: "approved", displayText: "Success" },
      vbms: { statusClass: "vbms", displayText: "VBMS" },
      error: { statusClass: "error", displayText: "Error" },
      expired: { statusClass: "expired", displayText: "Expired" }
    };

    const regularStatusMap = {
      PENDING: { statusClass: "pending", displayText: type === "ESign" ? "Pending" : "Review Pending" },
      APPROVED: { statusClass: "approved", displayText: "Approved" },
      REWORK: { statusClass: "rework", displayText: "Rework Requested" },
      Completed: { statusClass: "approved", displayText: "Completed" },
      COMPLETED: { statusClass: "approved", displayText: "Completed" },
      // Closed e-sign requests (the E-Signatures page lists them).
      CANCELLED: { statusClass: "expired", displayText: "Cancelled" },
      EXPIRED: { statusClass: "expired", displayText: "Expired" },
      DECLINED: { statusClass: "error", displayText: "Declined" },
      FAILED: { statusClass: "error", displayText: "Failed" },
    };

    // InboxTask statuses. Kept separate from regularStatusMap because
    // OPEN reads as "Not Started" here but would be wrong wording for a
    // review, and CANCELLED has no review equivalent at all.
    const assignmentStatusMap = {
      OPEN: { statusClass: "pending", displayText: "Not Started" },
      IN_PROGRESS: { statusClass: "rework", displayText: "In Progress" },
      COMPLETED: { statusClass: "approved", displayText: "Complete" },
      CANCELLED: { statusClass: "expired", displayText: "Cancelled" },
    };

    if (type === "Veteran") return veteranStatusMap[text] || { statusClass: "", displayText: "" };
    if (type === "assignment") {
      return assignmentStatusMap[String(text).toUpperCase()] ||
        { statusClass: "", displayText: "" };
    }
    return regularStatusMap[text] || { statusClass: "", displayText: "" };
  };
  
  export const renderStatus = (type, text='') => {
    const { statusClass, displayText } = getStatusInfo(text, type);
    return renderStatusWithClass(statusClass, displayText);
  };

  export const renderVeteransAffairsStatus = (text='') => {
    const { statusClass, displayText } = getStatusInfo(text, 'Veteran');
    return renderStatusWithClass(statusClass, displayText);
  };
  
  export const renderNotes = (text, search) => (
    <Tooltip placement="rightTop" title={text}>
      <TableTdWrapper>{text ? HighlightText(text, search) : "-"}</TableTdWrapper>
    </Tooltip>
  );
  
  export const renderSharedStatus = (record, type) => {
    const status = record.status;
    let completion = '';
  
    if (status === "PENDING" && record?.reviewers?.length > 1) {
      const pendingStatus = record?.reviewers.filter(reviewer => reviewer.status === 'PENDING');
      completion = `(${(record?.reviewers?.length - pendingStatus.length)}/${record?.reviewers?.length})`;
    }
  
    // Reuse the status logic from getStatusInfo
    const { statusClass, displayText } = getStatusInfo(status, type);
    const finalDisplayText = completion ? `${displayText} ${completion}` : displayText;
    
    return renderStatusWithClass(statusClass, finalDisplayText);
  };
  


export const sortableTitle = (label, field, onSort) => (
    <SortIconColumn>
      <span>{label}</span>
      {typeof onSort === "function" && (
        <Box
          component="button"
          type="button"
          tabIndex={0}
          onClick={() => onSort(field)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onSort(field);
            }
          }}
          className="sort-icon"
        >
          <img src={SortIcon} alt="Sorting Icon" />
        </Box>
      )}
    </SortIconColumn>
  );

export const fileNameColumn = (search, onSort) => ({
    title: sortableTitle("FILENAME", "file_name", onSort),
    dataIndex: "file_name",
    key: "file_name",
    render: (_, record) => {
      const name = record?.file_name || record?.filename || "";
      return renderFileName(name, search);
    },
  });
  
  export const assignedOnColumn = (title) => ({
    title: <Header>{title || "ASSIGNED ON"}</Header>,
    dataIndex: "assigned_on",
    key: "assigned_on",
    render: renderDate,
  });
  
  export const dueOnColumn = {
    title: <Header>DUE ON</Header>,
    dataIndex: "due_on",
    key: "due_on",
    render: (_, record) => renderDueOn(_, record),
  };
  
  export const notesColumn = (search) => ({
    title: <Header>NOTES</Header>,
    dataIndex: "notes",
    key: "notes",
    render: (text) => renderNotes(text, search),
  });

  export const matterColumn = {
    title: <Header>MATTER</Header>,
    dataIndex: "matter_name",
    key: "matter_name",
    render: (text, record) => renderText(text || record.matter_id || "-"),
  };
  